
export interface Question { id: string; question: string; type: string; options?: string[]; required: boolean; why: string; }
export interface QuestionSection { name: string; questions: Question[]; }

const QUESTION_BANKS: Record<string, QuestionSection[]> = {
  web_design: [
    {
      name: 'Business Context',
      questions: [
        { id: 'wd_biz_1', question: 'What industry does your business operate in?', type: 'select', options: ['E-commerce', 'SaaS', 'Healthcare', 'Finance', 'Education', 'Real Estate', 'Hospitality', 'Other'], required: true, why: 'Determines design conventions and compliance requirements' },
        { id: 'wd_biz_2', question: 'What is your primary business goal for this website?', type: 'select', options: ['Generate leads', 'Sell products', 'Build brand awareness', 'Provide information', 'Customer support', 'Other'], required: true, why: 'Drives information architecture and CTA strategy' },
        { id: 'wd_biz_3', question: 'Who is your target audience?', type: 'textarea', required: true, why: 'Informs user persona development and UX decisions' },
        { id: 'wd_biz_4', question: 'What makes your business different from competitors?', type: 'textarea', required: true, why: 'Shapes messaging hierarchy and brand positioning' },
        { id: 'wd_biz_5', question: 'Do you have existing brand guidelines?', type: 'select', options: ['Yes, comprehensive', 'Yes, basic', 'No, need help', 'Not sure'], required: false, why: 'Determines if brand discovery phase is needed' }
      ]
    },
    {
      name: 'Goals & Success Metrics',
      questions: [
        { id: 'wd_goal_1', question: 'What does success look like for this project?', type: 'textarea', required: true, why: 'Defines measurable project success criteria' },
        { id: 'wd_goal_2', question: 'What is your desired timeline?', type: 'select', options: ['ASAP (< 4 weeks)', '1-2 months', '3-4 months', 'Flexible', 'Not sure'], required: true, why: 'Determines project scope and resource allocation' },
        { id: 'wd_goal_3', question: 'What is your approximate budget range?', type: 'select', options: ['Under $5,000', '$5,000-$15,000', '$15,000-$50,000', '$50,000+', 'Prefer not to say'], required: true, why: 'Ensures proposal alignment with budget reality' },
        { id: 'wd_goal_4', question: 'Do you have a preference for technology stack?', type: 'multi_select', options: ['WordPress', 'Webflow', 'React/Next.js', 'Shopify', 'No preference', 'Not sure'], required: false, why: 'Informs technical approach and team assignment' }
      ]
    },
    {
      name: 'Constraints & Logistics',
      questions: [
        { id: 'wd_con_1', question: 'Are there any must-have features or integrations?', type: 'textarea', required: false, why: 'Identifies technical dependencies early' },
        { id: 'wd_con_2', question: 'Are there any design restrictions or preferences?', type: 'textarea', required: false, why: 'Prevents design direction conflicts' },
        { id: 'wd_con_3', question: 'Do you have content ready, or do you need content creation?', type: 'select', options: ['Content is ready', 'Need copywriting', 'Need photography', 'Need both', 'Not sure'], required: true, why: 'Affects timeline and pricing significantly' },
        { id: 'wd_con_4', question: 'Who are the key decision makers and stakeholders?', type: 'textarea', required: true, why: 'Identifies who needs to be involved in reviews' }
      ]
    }
  ],
  consulting: [
    {
      name: 'Business Context',
      questions: [
        { id: 'con_biz_1', question: 'What is the core challenge you are facing?', type: 'textarea', required: true, why: 'Defines engagement scope and approach' },
        { id: 'con_biz_2', question: 'What have you already tried to solve this?', type: 'textarea', required: true, why: 'Avoids redundant recommendations' },
        { id: 'con_biz_3', question: 'What does the ideal outcome look like in 6 months?', type: 'textarea', required: true, why: 'Sets engagement success metrics' },
        { id: 'con_biz_4', question: 'What internal resources do you have available?', type: 'multi_select', options: ['Dedicated project lead', 'Technical team', 'Budget for implementation', 'Executive sponsor', 'Time for workshops'], required: true, why: 'Determines implementation feasibility' }
      ]
    },
    {
      name: 'Goals & Success Metrics',
      questions: [
        { id: 'con_goal_1', question: 'What metrics will define success?', type: 'textarea', required: true, why: 'Quantifies engagement value' },
        { id: 'con_goal_2', question: 'What is your timeline for seeing results?', type: 'select', options: ['Immediate (< 1 month)', 'Short-term (1-3 months)', 'Medium-term (3-6 months)', 'Long-term (6-12 months)'], required: true, why: 'Aligns methodology with expectations' },
        { id: 'con_goal_3', question: 'What is your budget range for this engagement?', type: 'select', options: ['Under $10,000', '$10,000-$25,000', '$25,000-$75,000', '$75,000+', 'Prefer to discuss'], required: true, why: 'Proposes appropriate engagement model' }
      ]
    }
  ],
  marketing: [
    {
      name: 'Business Context',
      questions: [
        { id: 'mkt_biz_1', question: 'What is your current marketing stack?', type: 'multi_select', options: ['Website', 'Social media', 'Email marketing', 'SEO', 'PPC/Ads', 'Content marketing', 'None / Starting fresh'], required: true, why: 'Identifies gaps and integration needs' },
        { id: 'mkt_biz_2', question: 'Who is your ideal customer?', type: 'textarea', required: true, why: 'Drives targeting and messaging strategy' },
        { id: 'mkt_biz_3', question: 'What is your current customer acquisition cost (CAC)?', type: 'select', options: ['Under $50', '$50-$200', '$200-$500', '$500+', "Don't know"], required: false, why: 'Benchmarks against industry standards' }
      ]
    },
    {
      name: 'Goals & Success Metrics',
      questions: [
        { id: 'mkt_goal_1', question: 'What is your primary marketing objective?', type: 'select', options: ['Increase brand awareness', 'Generate leads', 'Convert leads to customers', 'Retain existing customers', 'Launch new product'], required: true, why: 'Determines campaign strategy and channels' },
        { id: 'mkt_goal_2', question: 'What is your monthly marketing budget?', type: 'select', options: ['Under $1,000', '$1,000-$5,000', '$5,000-$20,000', '$20,000+', 'Not established'], required: true, why: 'Proposes realistic channel mix' },
        { id: 'mkt_goal_3', question: 'What metrics matter most to you?', type: 'multi_select', options: ['Website traffic', 'Lead volume', 'Conversion rate', 'Cost per lead', 'ROI/ROAS', 'Brand awareness', 'Social engagement'], required: true, why: 'Focuses reporting on what client values' }
      ]
    }
  ]
};

export function getQuestionBank(serviceType: string): QuestionSection[] {
  return QUESTION_BANKS[serviceType] || QUESTION_BANKS.web_design;
}

export function detectRedFlags(answers: Record<string, string>): { severity: string; message: string }[] {
  const flags: { severity: string; message: string }[] = [];
  const vals = Object.values(answers).join(' ').toLowerCase();

  if (answers['wd_goal_2'] === 'ASAP (< 4 weeks)' || answers['con_goal_2'] === 'Immediate (< 1 month)') {
    flags.push({ severity: 'high', message: 'Extremely tight timeline — scope likely unrealistic or client disorganized' });
  }
  if (answers['wd_goal_3'] === 'Prefer not to say' || answers['con_goal_3'] === 'Prefer to discuss') {
    flags.push({ severity: 'medium', message: 'Client unwilling to share budget — may have unrealistic expectations or price shop' });
  }
  if (answers['wd_con_4'] && answers['wd_con_4'].length < 10) {
    flags.push({ severity: 'medium', message: 'Unclear decision-making process — risk of scope creep and delays' });
  }
  if (answers['wd_con_1'] && answers['wd_con_1'].split(',').length > 8) {
    flags.push({ severity: 'high', message: 'Excessive must-have features — scope likely too large for budget/timeline' });
  }
  if (answers['wd_con_3'] === 'Need both') {
    flags.push({ severity: 'medium', message: 'Needs content creation — adds 2-4 weeks and significant cost to timeline' });
  }
  if (/\b(asap|urgent|rush|emergency|yesterday)\b/.test(vals)) {
    flags.push({ severity: 'medium', message: 'Rush language detected — consider rush fee or timeline negotiation' });
  }
  if (/\b(unlimited|endless|as many|no limit|whatever|etc)\b/.test(vals)) {
    flags.push({ severity: 'high', message: 'Vague scope boundaries detected — risk of scope creep' });
  }

  return flags;
}

export function generateProjectBrief(serviceType: string, answers: Record<string, string>): string {
  const sections = getQuestionBank(serviceType);
  let brief = `# Project Brief\n\n**Service Type:** ${serviceType.replace('_', ' ')}\n**Date:** ${new Date().toLocaleDateString()}\n\n---\n\n`;
  sections.forEach(section => {
    brief += `## ${section.name}\n\n`;
    section.questions.forEach(q => {
      const answer = answers[q.id] || 'Not answered';
      brief += `**${q.question}**\n${answer}\n\n`;
    });
  });
  return brief;
}
